import md5 from 'js-md5';

class DeviceFingerprint {
  static _instance = null;

  _collectors = new Map();
  _deviceInfo = null;
  _deviceId = null;

  constructor() {
    if (DeviceFingerprint._instance) {
      return DeviceFingerprint._instance;
    }
    this._collectors
      .set('gpu', this._gpuInfo)
      .set('screen', this._screenInfo)
      .set('platform', this._platformInfo)
      .set('locale', this._localeInfo)
      .set('timezone', this._timezoneInfo)
      .set('touch', this._touchInfo);
    DeviceFingerprint._instance = this;
  }

  _gpuInfo() {
    let canvas = null;
    let gl = null;
    try {
      canvas = document.createElement('canvas');
      canvas.width = 8;
      canvas.height = 8;
      gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (!gl) return 'unknown';

      const compile = (type, src) => {
        const s = gl.createShader(type);
        gl.shaderSource(s, src);
        gl.compileShader(s);
        return s;
      };

      const vs = compile(gl.VERTEX_SHADER, 'attribute vec2 p;void main(){gl_Position=vec4(p,0.0,1.0);}');
      const fs = compile(gl.FRAGMENT_SHADER, 'precision highp float;void main(){vec2 c=gl_FragCoord.xy;float r=0.0;for(int i=0;i<32;i++){float n=float(i);r+=sin(c.x*0.12+n*0.7)*cos(c.y*0.13+n*0.5)+tan(c.x*0.02+c.y*0.03+n)*0.01;}vec4 v=vec4(fract(r),fract(r*1.7),fract(r*3.1),1.0);gl_FragColor=floor(v*255.0)/255.0;}');

      const prog = gl.createProgram();
      gl.attachShader(prog, vs);
      gl.attachShader(prog, fs);
      gl.linkProgram(prog);
      gl.useProgram(prog);

      const buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
      const loc = gl.getAttribLocation(prog, 'p');
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

      const pixels = new Uint8Array(256);
      gl.readPixels(0, 0, 8, 8, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

      const limits = gl.getParameter(gl.MAX_TEXTURE_SIZE) + '|' + gl.getParameter(gl.MAX_RENDERBUFFER_SIZE);

      let out = '';
      for (let i = 0; i < 256; i++) out += (pixels[i] + 256).toString(16).slice(1);
      return out + '|' + limits;
    } catch {
      return 'unknown';
    } finally {
      if (gl) {
        const ext = gl.getExtension('WEBGL_lose_context');
        if (ext) ext.loseContext();
      }
      if (canvas) { canvas.width = 0; canvas.height = 0; }
    }
  }

  _screenInfo() {
    return [
      window.screen.width,
      window.screen.height,
      window.devicePixelRatio,
    ].join('|');
  }

  _platformInfo() {
    return String(window.navigator.platform || 'unknown');
  }

  _localeInfo() {
    const langs = Array.isArray(navigator.languages) ? navigator.languages : [navigator.language];
    return String(langs[0] || 'unknown');
  }

  _timezoneInfo() {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const offset = -new Date().getTimezoneOffset();
    return `${tz}|${offset}`;
  }

  _touchInfo() {
    return String(window.navigator.maxTouchPoints || 0);
  }

  getInfo() {
    if (this._deviceInfo) {
      return this._deviceInfo;
    }
    const info = {};
    this._collectors.forEach((fn, key) => {
      info[key] = fn();
    });
    this._deviceInfo = info;
    return info;
  }

  getRaw() {
    const info = this.getInfo();
    return info.gpu + info.screen + info.platform + info.locale + info.timezone + info.touch;
  }

  getId() {
    if (this._deviceId) {
      return this._deviceId;
    }
    const raw = this.getRaw();
    const id = md5.hex(raw);
    console.log('🚀 ~ DeviceFingerprint ~ getId ~ id:', id);
    this._deviceId = id;
    return id;
  }

  static getInstance() {
    if (!DeviceFingerprint._instance) {
      DeviceFingerprint._instance = new DeviceFingerprint();
    }
    return DeviceFingerprint._instance;
  }
}

export const device = DeviceFingerprint.getInstance();

export const getDeviceInfo = () => device.getInfo();
export const getDeviceRaw = () => device.getRaw();
export const getDeviceId = () => device.getId();